import React from 'react';
import { Menu, Button, Space, Tooltip } from 'antd';
import { useRef } from 'react';
import {
  ThunderboltOutlined,
  RobotOutlined,
  FormOutlined,
  SendOutlined,
  SaveOutlined,
  PlayCircleOutlined,
  MailOutlined,
  BranchesOutlined,
  FileAddOutlined,
  BarChartOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  EditOutlined,
  LineChartOutlined,
  FontSizeOutlined,
  CopyOutlined,
  SearchOutlined,
  FireOutlined,
  FileDoneOutlined,
  UserOutlined,
  ImportOutlined,
  MessageOutlined,
  ApiOutlined,
  PictureOutlined,
  PushpinOutlined,
  FileOutlined,
  ShopOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { Video } from 'lucide-react';

const { SubMenu } = Menu;

const Sidebar = ({ 
  updateNodeData,  
  onImport, 
  onSave, 
  onExecute, 
  onUpdate, 
  onSaveAsTemplate, 
  handleSaveEditedTemplate, 
  isEditMode, 
  isExecuting, 
  isEditingTemplate, 
  isSavedWorkflow, 
  workflowName, 
  onActionModalOpen,
  darkMode
}) => {
  const fileInputRef = useRef(null);

  const colors = {
    background: darkMode ? '#1f1f1f' : '#ffffff',
    border: darkMode ? '#333333' : '#f0f0f0',
    text: darkMode ? '#ffffff' : '#000000',
    menuBackground: darkMode ? '#141414' : '#ffffff',
    buttonBorder: darkMode ? '#177ddc' : '#1677ff',
    primaryButtonBg: darkMode ? '#177ddc' : '#1677ff',
    primaryButtonHoverBg: darkMode ? '#1e88e5' : '#4096ff',
    secondaryButtonBg: darkMode ? '#141414' : '#ffffff',
    secondaryButtonText: darkMode ? '#177ddc' : '#1677ff',
    secondaryButtonHoverBg: darkMode ? '#1f1f1f' : '#e6f4ff',
  };
  
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };


  const buttonStyle = {
    width: '100%',
    marginBottom: '8px',
    borderRadius: '6px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: `1px solid ${colors.buttonBorder}`,
    transition: 'all 0.2s ease',
    boxShadow: darkMode ? 'none' : '0 2px 0 rgba(0, 0, 0, 0.02)'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: colors.primaryButtonBg,
    color: '#ffffff',
    '&:hover': {
      backgroundColor: colors.primaryButtonHoverBg,
      borderColor: colors.primaryButtonHoverBg
    }
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: colors.secondaryButtonBg,
    color: colors.secondaryButtonText,
    '&:hover': {
      backgroundColor: colors.secondaryButtonHoverBg,
      borderColor: colors.buttonBorder
    }
  };

  // Show execute button if workflow is saved or in edit mode, and not editing a template
  const showExecuteButton = (isSavedWorkflow || isEditMode || workflowName) && !isEditingTemplate;

  return (
    <div className="workflow-sidebar" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      borderRight: `1px solid ${colors.border}`,
      background: colors.background,
      color: colors.text
    }}>
      <div className="sidebar-scroll" style={{ flex: 1, overflowY: 'auto' }}>
        <Menu 
          mode="vertical" 
          style={{ 
            width: '100%',
            border: 'none',
            background: colors.menuBackground,
            color: colors.text
          }}
          theme={darkMode ? 'dark' : 'light'}
        >
          <Menu.Item 
            key="trigger" 
            icon={<ThunderboltOutlined />} 
            draggable 
            onDragStart={(event) => onDragStart(event, 'trigger')}
          >
            Trigger
          </Menu.Item>
        <div className="ai-actions-menu">
        <SubMenu key="aiActions" icon={<RobotOutlined />} title="AI Actions">
            <Menu.Item key="aiModel" icon={<RobotOutlined />} draggable onDragStart={(event) => onDragStart(event, 'aiModel')}>
              AI Model
            </Menu.Item>
            <Menu.Item key="sentimentAnalysis" icon={<BarChartOutlined />} draggable onDragStart={(event) => onDragStart(event, 'sentimentAnalysis')}>
              Sentiment Analysis
            </Menu.Item>
            <Menu.Item key="summaryGenerator" icon={<FileTextOutlined />} draggable onDragStart={(event) => onDragStart(event, 'summaryGenerator')}>
              Summary Generator
            </Menu.Item>
            <Menu.Item key="extractInformation" icon={<FileSearchOutlined />} draggable onDragStart={(event) => onDragStart(event, 'extractInformation')}>
              Extract Information
            </Menu.Item>
            <Menu.Item key="writeArticle" icon={<FontSizeOutlined />} draggable onDragStart={(event) => onDragStart(event, 'writeArticle')}>
              Write an Article
            </Menu.Item>
            <Menu.Item key="optimizeSEO" icon={<LineChartOutlined />} draggable onDragStart={(event) => onDragStart(event, 'optimizeSEO')}>
              Optimize for SEO
            </Menu.Item>
            <Menu.Item key="research" icon={<SearchOutlined />} draggable onDragStart={(event) => onDragStart(event, 'research')}>
              Research 
            </Menu.Item>
          </SubMenu></div>
          <Menu.Item key="mediaGenerator" icon={<VideoCameraOutlined />} draggable onDragStart={(event) => onDragStart(event, 'mediaGenerator')}>
            Media Generator
          </Menu.Item>
          <Menu.Item key="firecrawl" icon={<FireOutlined />} draggable onDragStart={(event) => onDragStart(event, 'firecrawl')}>
            Web Scraper/Crawler
          </Menu.Item>
        <Menu.Item key="parser" icon={<FileDoneOutlined />} draggable onDragStart={(event) => onDragStart(event, 'parser')}>
          Document Parser
        </Menu.Item>
        <Menu.Item key="humanInput" icon={<UserOutlined />} draggable onDragStart={(event) => onDragStart(event, 'humanInput')}>
          Human Input
        </Menu.Item>
        <Menu.Item key="post" icon={<PushpinOutlined />} draggable onDragStart={(event) => onDragStart(event, 'post')}>
          Create Post
        </Menu.Item>
        <Menu.Item 
          key="createFile" 
          icon={<FileAddOutlined />} 
          draggable 
          onDragStart={(event) => onDragStart(event, 'createFile')}
        >
          Create File
        </Menu.Item>
        <Menu.Item 
            key="unsplash" 
            icon={<PictureOutlined />} 
            draggable 
            onDragStart={(event) => onDragStart(event, 'unsplash')}
          >
            Unsplash Image
          </Menu.Item>
        <Menu.Item key="sendEmail" icon={<MailOutlined />} draggable onDragStart={(event) => onDragStart(event, 'sendEmail')}>
          Send Email
        </Menu.Item>
        <Menu.Item key="condition" icon={<BranchesOutlined />} draggable onDragStart={(event) => onDragStart(event, 'condition')}>
          Condition
        </Menu.Item>
        <Menu.Item key="chat" icon={<MessageOutlined />} draggable onDragStart={(event) => onDragStart(event, 'chat')}>
          Chatbot
        </Menu.Item>
        <Menu.Item key="APICall" icon={<ApiOutlined />} draggable onDragStart={(event) => onDragStart(event, 'APICall')}>
          API Call
        </Menu.Item>
        <div className="node-selector-output">
        <Menu.Item  key="output" icon={<SendOutlined />} draggable onDragStart={(event) => onDragStart(event, 'output')}>
          Output
        </Menu.Item></div>
      </Menu>
    </div>


      {/* Buttons section */}
      <div style={{ 
        padding: '16px', 
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.background
      }}>
        {/* Update/Save Workflow Button */}
        {isEditMode || isSavedWorkflow ? (
          <Button 
            icon={<EditOutlined />} 
            onClick={onUpdate}
            style={secondaryButtonStyle}
            className={`workflow-button ${darkMode ? 'dark' : ''}`}
          >
            Update Workflow
          </Button>
        ) : (
          <Button 
            icon={<SaveOutlined />} 
            onClick={onSave}
            type="primary"
            style={primaryButtonStyle}
            className={`save-workflow-button workflow-button ${darkMode ? 'dark' : ''}`}
          >
            Save Workflow
          </Button>
        )}

        {/* Execute Button */}
        {showExecuteButton && (
          <Button 
            icon={<PlayCircleOutlined />} 
            onClick={onExecute}
            type="primary"
            loading={isExecuting}
            disabled={isExecuting}
            style={primaryButtonStyle}
            className={`execute-workflow-button workflow-button ${darkMode ? 'dark' : ''}`}
          >
            {isExecuting ? 'Executing...' : 'Execute Workflow'}
          </Button>
        )}

        {/* Template Buttons */}
        {isEditingTemplate && (
          <Button 
            icon={<SaveOutlined />}
            onClick={handleSaveEditedTemplate}
            style={secondaryButtonStyle}
            className={`workflow-button ${darkMode ? 'dark' : ''}`}
          >
            Save Edited Template
          </Button>
        )}

        <Button 
          icon={<CopyOutlined />} 
          onClick={onSaveAsTemplate}
          style={secondaryButtonStyle}
          className={`workflow-button ${darkMode ? 'dark' : ''}`}
        >
          Save as Template
        </Button>

        <Button 
          icon={<ImportOutlined />} 
          onClick={onActionModalOpen}
          style={secondaryButtonStyle}
          className={`workflow-button ${darkMode ? 'dark' : ''}`}
        >
          Import/Export
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;